import axiosInstance from './axios';
import { User, PaginatedResponse, UserRole } from '../../types';

export interface CreateUserDto {
  email: string;
  role: UserRole;
  employeeId?: number;
  tempPassword?: string;
  isActive?: boolean;
}

export interface UpdateUserDto {
  email?: string;
  role?: UserRole;
  employeeId?: number | null;
  isActive?: boolean;
}

export enum UserAction {
  LOCK = 'LOCK',
  UNLOCK = 'UNLOCK',
  SEND_RESET_LINK = 'SEND_RESET_LINK',
  RESET_TEMP_PASSWORD = 'RESET_TEMP_PASSWORD',
}

export interface UserActionDto {
  action: UserAction;
  tempPassword?: string;
}

export interface UserFilters {
  page?: number;
  pageSize?: number;
  q?: string;
  role?: UserRole;
  status?: 'active' | 'inactive';
}

export interface UserDetail extends User {
  employee?: {
    id: number;
    employeeCode: string;
    fullName: string;
    email: string;
  };
}

class UsersService {
  private basePath = '/users';

  /**
   * Get all users with pagination and filters
   */
  async getAll(filters?: UserFilters): Promise<PaginatedResponse<UserDetail>> {
    const response = await axiosInstance.get<PaginatedResponse<UserDetail>>(this.basePath, {
      params: filters,
    });
    return response.data;
  }

  /**
   * Get user by ID
   */
  async getById(id: number): Promise<UserDetail> {
    const response = await axiosInstance.get<UserDetail>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new user
   */
  async create(data: CreateUserDto): Promise<User> {
    const response = await axiosInstance.post<User>(this.basePath, data);
    return response.data;
  }

  /**
   * Update a user
   */
  async update(id: number, data: UpdateUserDto): Promise<User> {
    const response = await axiosInstance.patch<User>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Perform user action (lock, unlock, reset password)
   */
  async performAction(id: number, data: UserActionDto): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.post<{ success: boolean; message: string }>(
      `${this.basePath}/${id}/actions`,
      data
    );
    return response.data;
  }
}

export const usersService = new UsersService();
